import React, { useRef, useState } from "react";
import { maxLogoBytes } from "@veyso/core";
import { api } from "../api.js";
import { ColorField, ErrorNote } from "../components/ui.js";
import type { LookColors } from "./wizard.js";

/**
 * Choosing a logo for one film, and storing it.
 *
 * Shared by the two places a film's look can be changed — an AI summary's
 * upload and a read site's — so both hold a logo to the same size limit and
 * store it the same way. Stored, not kept as a data URI: the start request
 * carries the key, and the server reads the bytes back itself.
 */
export const LogoUploadButton: React.FC<{
  hasLogo: boolean;
  /** Primary when choosing a logo is what stands between the customer and the next step. */
  primary?: boolean;
  onUploaded: (stored: { logoKey: string; logoUrl: string }) => void;
}> = ({ hasLogo, primary = false, onUploaded }) => {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const choose = async (file: File) => {
    setError(null);
    if (file.size > maxLogoBytes) {
      setError(`A logo must be under ${Math.round(maxLogoBytes / 1024)}KB`);
      return;
    }
    setBusy(true);
    try {
      const dataUri = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("That file could not be read"));
        reader.readAsDataURL(file);
      });
      onUploaded(await api.uploadBrandLogo(dataUri));
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
      // So choosing the same file twice in a row still fires a change.
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        className={`btn w-fit ${primary ? "btn-primary" : "btn-secondary"}`}
        disabled={busy}
        onClick={() => input.current?.click()}
      >
        {busy ? "Uploading…" : hasLogo ? "Replace logo" : "Choose a logo"}
      </button>
      <input
        ref={input}
        type="file"
        accept="image/svg+xml,image/png,image/webp,image/jpeg"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void choose(file);
        }}
      />
      {error ? <ErrorNote>{error}</ErrorNote> : null}
    </div>
  );
};

/**
 * The four colours a film is drawn in, and what the film uses each for.
 *
 * Said next to every swatch, because "second accent" on its own tells nobody
 * whether changing it matters. The uses are the ones the film's prompt gives
 * them (packages/core/src/launch-prompt.ts).
 */
export const colourRoles: { key: keyof LookColors; label: string; use: string }[] = [
  { key: "accent", label: "Accent", use: "Highlights and the logo's moments" },
  { key: "accentAlt", label: "Second accent", use: "A second highlight, used sparingly" },
  { key: "background", label: "Background", use: "Behind everything" },
  { key: "text", label: "Text", use: "Headlines and words on screen" },
];

/** The four colours a film is drawn in, each editable. */
export const LookColourFields: React.FC<{ colors: LookColors; onChange: (colors: LookColors) => void }> = ({
  colors,
  onChange,
}) => (
  <div className="grid grid-cols-2 gap-3">
    {colourRoles.map((role) => (
      <div key={role.key} className="flex flex-col gap-1">
        <ColorField
          label={role.label}
          value={colors[role.key]}
          onChange={(value) => onChange({ ...colors, [role.key]: value })}
        />
        <span className="text-[10.5px] text-ink-400">{role.use}</span>
      </div>
    ))}
  </div>
);
